import Layout from '../../components/common/Layout'
import dynamic from 'next/dynamic'
import HeaderNew from '../../components/home-new/HeaderNew'
import T from '../../components/home-new/tokens'
import HeroBiz from '../../components/home-business/HeroBiz'
const BusinessTypesBiz = dynamic(() => import('../../components/home-business/BusinessTypesBiz'))
const BenefitsBiz = dynamic(() => import('../../components/home-business/BenefitsBiz'))
const MethodsBiz = dynamic(() => import('../../components/home-business/MethodsBiz'))
const ConnectBiz = dynamic(() => import('../../components/home-business/ConnectBiz'))
// Section 6 — "ธุรกิจที่ไว้วางใจ SHIPPOP BUSINESS" reuses the shared ClientsNew
// grid (same 6-column pill layout AND the same logo set as /new — no `items`
// override), keeping only the business heading/subtitle/background.
const ClientsNew = dynamic(() => import('../../components/home-new/ClientsNew'))
const StepsBiz = dynamic(() => import('../../components/home-business/StepsBiz'))
const ContactFormBiz = dynamic(() => import('../../components/home-business/ContactFormBiz'))
const FaqBiz = dynamic(() => import('../../components/home-business/FaqBiz'))
const FooterNew = dynamic(() => import('../../components/home-new/FooterNew'))
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'

const SEO_TITLE = 'SHIPPOP BUSINESS โซลูชันจัดส่งสำหรับธุรกิจ ส่งพัสดุจำนวนมาก เชื่อมต่อ API | ลูกค้าองค์กร'
const SEO_DESCRIPTION = 'SHIPPOP BUSINESS โซลูชันจัดส่งสำหรับธุรกิจที่ต้องการส่งพัสดุจำนวนมาก จัดการง่าย เชื่อมต่อระบบด้วย API และ Plugin รองรับระบบวางบิล เอกสารทางบัญชี ผู้ดูแลธุรกิจโดยเฉพาะ พร้อมเรทราคาพิเศษสำหรับลูกค้าธุรกิจ'
const SEO_KEYWORDS = 'SHIPPOP Business, ลูกค้าองค์กร, ส่งพัสดุจำนวนมาก, เชื่อมต่อ API ขนส่ง, plugin ขนส่ง, WooCommerce ขนส่ง, Magento ขนส่ง, ระบบวางบิล, โซลูชันธุรกิจ, ขนส่งธุรกิจ, corporate shipping'

const structuredData = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'SHIPPOP BUSINESS',
  serviceType: 'Business Shipping Solution',
  url: 'https://www.shippop.com/business',
  description: SEO_DESCRIPTION,
  provider: {
    '@type': 'Organization',
    name: 'SHIPPOP',
    url: 'https://www.shippop.com',
    logo: 'https://www.shippop.com/static/img/sp-logo.svg',
  },
  areaServed: 'TH',
}

export async function getServerSideProps(context) {
  return {
    props: {
      ...(await serverSideTranslations(context.locale, [
        'common',
        'header',
        'footer',
        'pdpa',
        'home',
        'business',
      ])),
    },
  }
}

export default function BusinessHome() {
  return (
    <Layout
      title={SEO_TITLE}
      description={SEO_DESCRIPTION}
      keywords={SEO_KEYWORDS}
      noHeader
      noFooter
      headEl={
        <>
          {/* Preconnect the LINE add-friend widget origin (FooterNew) and
              preload the LCP hero (now a ~52 KB webp) — collapses LCP. */}
          <link rel="preconnect" href="https://d.line-cdn.net" crossOrigin="anonymous" />
          <link rel="dns-prefetch" href="https://d.line-cdn.net" />
          <link
            rel="preload"
            as="image"
            href="/static/img/home-business/hero.webp"
            type="image/webp"
            fetchpriority="high"
          />
          <link
            rel="preload"
            href="/static/fonts/sukhumvitset/SukhumvitSet-Text.woff2"
            as="font"
            type="font/woff2"
            crossOrigin="anonymous"
          />
          <link
            rel="preload"
            href="/static/fonts/sukhumvitset/SukhumvitSet-Medium.woff2"
            as="font"
            type="font/woff2"
            crossOrigin="anonymous"
          />
          <style
            dangerouslySetInnerHTML={{
              __html: `
@font-face{font-family:'Sukhumvit Set';src:url('/static/fonts/sukhumvitset/SukhumvitSet-Text.woff2') format('woff2');font-weight:400;font-style:normal;font-display:swap;}
@font-face{font-family:'Sukhumvit Set';src:url('/static/fonts/sukhumvitset/SukhumvitSet-Medium.woff2') format('woff2');font-weight:500;font-style:normal;font-display:swap;}
@font-face{font-family:'Sukhumvit Set';src:url('/static/fonts/sukhumvitset/SukhumvitSet-SemiBold.woff2') format('woff2');font-weight:600;font-style:normal;font-display:swap;}
@font-face{font-family:'Sukhumvit Set';src:url('/static/fonts/sukhumvitset/SukhumvitSet-Bold.woff2') format('woff2');font-weight:700;font-style:normal;font-display:swap;}
@property --promo-angle{syntax:'<angle>';initial-value:0deg;inherits:false;}
@keyframes promo-spin{to{--promo-angle:360deg;}}
`,
            }}
          />
          <link rel="canonical" href="https://www.shippop.com/business" />
          <meta key="og:type" property="og:type" content="website" />
          <meta key="og:title" property="og:title" content={SEO_TITLE} />
          <meta key="og:description" property="og:description" content={SEO_DESCRIPTION} />
          <meta key="og:image" property="og:image" content="https://www.shippop.com/static/img/home-business/hero.png" />
          <meta key="og:url" property="og:url" content="https://www.shippop.com/business" />
          <meta key="og:site_name" property="og:site_name" content="SHIPPOP" />
          <meta key="og:locale" property="og:locale" content="th_TH" />
          <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={SEO_TITLE} />
          <meta name="twitter:description" content={SEO_DESCRIPTION} />
          <meta name="twitter:image" content="https://www.shippop.com/static/img/home-business/hero.png" />
          <meta name="robots" content="index, follow" />
          <meta name="author" content="SHIPPOP" />
          <link rel="preload" href="/static/img/home-business/hero.png" as="image" />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
          />
        </>
      }
    >
      <HeaderNew />
      <main>
        <HeroBiz />
        <BusinessTypesBiz />
        <BenefitsBiz />
        <MethodsBiz />
        <ConnectBiz />
        <ClientsNew
          title={
            <>
              ธุรกิจที่ไว้วางใจ <span>SHIPPOP BUSINESS</span>
            </>
          }
          subtitle="ความไว้วางใจจากธุรกิจหลากหลายรูปแบบ ที่พร้อมเติบโตไปด้วยกัน"
          background={T.color.canvas}
        />
        <StepsBiz />
        <ContactFormBiz />
        <FaqBiz />
      </main>
      <FooterNew />
    </Layout>
  )
}
